import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Product from '../Product/Product';
import { applyFilters as applyHardcodedFilters } from '../../../store/slices/featuresSlice';
import styles from './ProductList.module.css';

/**
 * Extracts the level/tier number from a warranty string
 * Handles formats like "Level 2 - LVP", "Bathroom Tile Tier 3", etc.
 * @param {string} warranty - The warranty string
 * @returns {number} - The extracted level/tier number, or 999 if not found
 */
const extractWarrantyLevel = (warranty) => {
    if (!warranty) return 999;
    
    // Match "Level X" or "Tier X" (case insensitive)
    const match = warranty.match(/(?:level|tier)\s*(\d+)/i);
    if (match) {
        return parseInt(match[1], 10);
    }
    return 999;
};

/**
 * Groups products by their warranty field
 * @param {Array} products - Array of product objects
 * @returns {Array} - Array of group objects { label, level, products }
 */
const groupProductsByWarranty = (products) => {
    const groups = {};
    
    products.forEach((product) => {
        const warranty = product.warranty || '';
        const label = warranty.trim() || 'Other';
        
        if (!groups[label]) {
            groups[label] = {
                label,
                level: extractWarrantyLevel(label),
                products: []
            };
        }
        
        groups[label].products.push(product);
    });
    
    // Convert to array and sort by level number
    return Object.values(groups).sort((a, b) => a.level - b.level);
};

/**
 * Groups products by a specified column (e.g., categoryName)
 * @param {Array} products - Array of product objects
 * @param {string} column - Column name to group by
 * @param {Array} order - Optional array of values to define sort order (values not in array go last)
 * @returns {Array} - Array of group objects { label, products }
 */
const groupProductsByColumn = (products, column, order = []) => {
    const groups = {};
    
    products.forEach((product) => {
        const value = product[column] || '';
        const label = value.trim() || 'Other';
        
        if (!groups[label]) {
            groups[label] = {
                label,
                products: []
            };
        }
        
        groups[label].products.push(product);
    });
    
    // Convert to array and sort by provided order (or alphabetically if no order)
    const groupArray = Object.values(groups);
    
    if (order.length > 0) {
        return groupArray.sort((a, b) => {
            const aIndex = order.indexOf(a.label);
            const bIndex = order.indexOf(b.label);
            // Items not in order array go to end
            const aOrder = aIndex === -1 ? 999 : aIndex;
            const bOrder = bIndex === -1 ? 999 : bIndex;
            return aOrder - bOrder;
        });
    }
    
    return groupArray.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * ProductList - Displays a filterable, searchable list of products
 * 
 * @param {string} seriesId - Series ID to fetch products from (single series)
 * @param {Array} seriesIds - Array of series IDs to fetch products from (multiple series, merged)
 * @param {string} thumbSize - Thumbnail size (default: "256")
 * @param {Object} filters - Filter rules in format { key: value } to match exact values
 * @param {Array} hardcodedFilters - Array of filter objects with { column, value, operator } for advanced filtering
 * @param {Array} allowedIds - Array of product IDs to allow (OR logic - product matches if its ID is in this array)
 * @param {string} searchValue - Search term to match across multiple columns
 * @param {Array} searchColumns - Array of column names to search in (default: ['name', 'color', 'description'])
 * @param {Function} onProductSelect - Callback when product is selected
 * @param {string} titleAttribute - Product attribute to use as title (default: 'color')
 * @param {string} descriptionAttribute - Product attribute to use as description (default: 'name')
 * @param {Function} transformTitle - Optional function to transform title text before display
 * @param {Function} transformDescription - Optional function to transform description text before display
 * @param {string} sortBy - Product attribute to sort by (optional)
 * @param {string} sortOrder - Sort order: 'asc' or 'desc' (default: 'asc')
 * @param {string} className - Additional CSS class
 * @param {boolean} groupByWarranty - Whether to group products by warranty field (default: false)
 * @param {string} groupByColumn - Column name to group products by (e.g., 'categoryName')
 * @param {Array} groupByColumnOrder - Optional array defining sort order for groups (e.g., ['Stone', 'Brick'])
 */
export default function ProductList({ 
    seriesId, 
    seriesIds,
    thumbSize = "256",
    filters = {}, 
    hardcodedFilters = [],
    allowedIds = null,
    searchValue = "",
    searchColumns = ['name', 'color', 'description', 'manufacturerSeriesName'],
    onProductSelect,
    titleAttribute = 'color',
    descriptionAttribute = 'name',
    transformTitle,
    transformDescription,
    sortBy = null,
    sortOrder = 'asc',
    className = "",
    gridClassName = "",
    selectedProductId: externalSelectedId = null,
    groupByWarranty = false,
    groupByColumn = null,
    groupByColumnOrder = []
}) {
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [internalSelectedId, setInternalSelectedId] = useState(null);
    
    // Use external selectedProductId if provided, otherwise use internal state
    const selectedProductId = externalSelectedId !== null ? externalSelectedId : internalSelectedId;

    // Reset internal selection when external selection changes (e.g., surface tab switch)
    useEffect(() => {
        setInternalSelectedId(externalSelectedId);
    }, [externalSelectedId]);

    // Determine which series to fetch (support both single and multiple)
    const effectiveSeriesIds = useMemo(() => {
        if (seriesIds && seriesIds.length > 0) return seriesIds;
        if (seriesId) return [seriesId];
        return [];
    }, [seriesId, seriesIds]);

    // Fetch product data (supports multiple series IDs)
    useEffect(() => {
        if (effectiveSeriesIds.length === 0) return;

        setLoading(true);
        setError(null);

        // Fetch all series in parallel
        const fetchPromises = effectiveSeriesIds.map(id => {
            const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/${id}.json`;
            return fetch(URL)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch products for series ${id}: ${response.status}`);
                    }
                    return response.json();
                })
                .then(data => Array.isArray(data) ? data : []);
        });

        Promise.all(fetchPromises)
            .then(results => {
                // Merge all products from all series
                const mergedProducts = results.flat();
                setProductData(mergedProducts);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching products:', error);
                setError(error.message);
                setLoading(false);
            });
    }, [effectiveSeriesIds.join(',')]);

    // Apply filters
    const applyFilters = (products) => {
        if (!filters || Object.keys(filters).length === 0) {
            return products;
        }

        return products.filter(product => {
            // Check if product matches all filter rules
            return Object.entries(filters).every(([key, value]) => {
                const productValue = product[key];
                
                // Handle null/undefined
                if (productValue === null || productValue === undefined) {
                    return false;
                }
                
                // Convert to string for comparison
                return String(productValue).toLowerCase() === String(value).toLowerCase();
            });
        });
    };

    // Apply search
    const applySearch = (products) => {
        if (!searchValue || searchValue.trim() === '') {
            return products;
        }

        const searchLower = searchValue.toLowerCase().trim();

        return products.filter(product => {
            // Check if search term matches any of the specified columns (partial match)
            return searchColumns.some(column => {
                const columnValue = product[column];
                
                // Handle null/undefined
                if (columnValue === null || columnValue === undefined) {
                    return false;
                }
                
                // Convert to string and check for partial match
                return String(columnValue).toLowerCase().includes(searchLower);
            });
        });
    };

    // Apply sorting
    const applySort = (products) => {
        if (!sortBy) return products;

        return [...products].sort((a, b) => {
            const aVal = a[sortBy] || '';
            const bVal = b[sortBy] || '';
            
            const comparison = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
            return sortOrder === 'desc' ? -comparison : comparison;
        });
    };

    // Get filtered and searched products
    const getFilteredProducts = () => {
        let filtered = productData;
        // Apply allowedIds filter first (OR logic - product ID must be in the array)
        if (allowedIds && allowedIds.length > 0) {
            filtered = filtered.filter(product => allowedIds.includes(product.id));
        }
        // Apply hardcoded filters first (with operator support)
        filtered = filtered.filter(product => applyHardcodedFilters(product, hardcodedFilters));
        // Apply simple key-value filters
        filtered = applyFilters(filtered);
        filtered = applySearch(filtered);
        filtered = applySort(filtered);
        return filtered;
    };

    const filteredProducts = getFilteredProducts();
    
    // Group products by warranty if enabled
    const groupedProducts = useMemo(() => {
        if (!groupByWarranty) return null;
        return groupProductsByWarranty(filteredProducts);
    }, [filteredProducts, groupByWarranty]);

    // Group products by column if enabled
    const columnGroupedProducts = useMemo(() => {
        if (!groupByColumn) return null;
        return groupProductsByColumn(filteredProducts, groupByColumn, groupByColumnOrder);
    }, [filteredProducts, groupByColumn, groupByColumnOrder]);

    // Handle product selection
    const handleProductSelect = (id) => {
        const selectedProduct = productData.find(product => product.id === id);
        if (selectedProduct && onProductSelect) {
            setInternalSelectedId(id);
            onProductSelect(selectedProduct);
        }
    };

    // Render loading state
    if (loading) {
        return (
            <div className={`${styles.productList} ${className}`}>
                <div className={styles.message}>Loading products...</div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className={`${styles.productList} ${className}`}>
                <div className={styles.errorMessage}>Error: {error}</div>
            </div>
        );
    }

    // Render product list
    return (
        <div className={`${styles.productList} ${className}`} data-series={seriesId}>
            {filteredProducts.length > 0 ? (
                groupByWarranty && groupedProducts ? (
                    // Render grouped by warranty
                    <div className={styles.groupedContainer}>
                        {groupedProducts.map((group) => (
                            <div key={group.label} className={styles.warrantyGroup}>
                                {group.label !== 'Other' && (
                                    <div className={styles.sectionDivider}>{group.label}</div>
                                )}
                                <ul className={`${styles.productGrid} ${gridClassName}`}>
                                    {group.products.map(product => {
                                        const title = product[titleAttribute] || '';
                                        const description = product[descriptionAttribute] || '';
                                        
                                        return (
                                            <Product
                                                key={product.id}
                                                id={product.id}
                                                product={product}
                                                size={thumbSize}
                                                title={transformTitle ? transformTitle(title) : title}
                                                description={transformDescription ? transformDescription(description) : description}
                                                onSelect={handleProductSelect}
                                                isActive={product.id === selectedProductId}
                                            />
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                ) : groupByColumn && columnGroupedProducts ? (
                    // Render grouped by column (e.g., categoryName)
                    <div className={styles.groupedContainer}>
                        {columnGroupedProducts.map((group) => (
                            <div key={group.label} className={styles.warrantyGroup}>
                                <div className={styles.sectionDivider}>{group.label}</div>
                                <ul className={`${styles.productGrid} ${gridClassName}`}>
                                    {group.products.map(product => {
                                        const title = product[titleAttribute] || '';
                                        const description = product[descriptionAttribute] || '';
                                        
                                        return (
                                            <Product
                                                key={product.id}
                                                id={product.id}
                                                product={product}
                                                size={thumbSize}
                                                title={transformTitle ? transformTitle(title) : title}
                                                description={transformDescription ? transformDescription(description) : description}
                                                onSelect={handleProductSelect}
                                                isActive={product.id === selectedProductId}
                                            />
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                ) : (
                    // Render flat list (no grouping)
                    <ul className={`${styles.productGrid} ${gridClassName}`}>
                        {filteredProducts.map(product => {
                            const title = product[titleAttribute] || '';
                            const description = product[descriptionAttribute] || '';
                            
                            return (
                                <Product
                                    key={product.id}
                                    id={product.id}
                                    product={product}
                                    size={thumbSize}
                                    title={transformTitle ? transformTitle(title) : title}
                                    description={transformDescription ? transformDescription(description) : description}
                                    onSelect={handleProductSelect}
                                    isActive={product.id === selectedProductId}
                                />
                            );
                        })}
                    </ul>
                )
            ) : (
                <div className={styles.emptyMessage}>
                    {searchValue || Object.keys(filters).length > 0 
                        ? 'No products match your criteria.' 
                        : 'No products available.'}
                </div>
            )}
        </div>
    );
}

ProductList.propTypes = {
    seriesId: PropTypes.string,
    seriesIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    thumbSize: PropTypes.string,
    filters: PropTypes.object,
    hardcodedFilters: PropTypes.arrayOf(PropTypes.shape({
        column: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        operator: PropTypes.oneOf(['equals', 'contains', 'startsWith', 'endsWith'])
    })),
    allowedIds: PropTypes.arrayOf(PropTypes.number),
    searchValue: PropTypes.string,
    searchColumns: PropTypes.arrayOf(PropTypes.string),
    onProductSelect: PropTypes.func,
    titleAttribute: PropTypes.string,
    descriptionAttribute: PropTypes.string,
    transformTitle: PropTypes.func,
    transformDescription: PropTypes.func,
    sortBy: PropTypes.string,
    sortOrder: PropTypes.oneOf(['asc', 'desc']),
    className: PropTypes.string,
    gridClassName: PropTypes.string,
    selectedProductId: PropTypes.number,
    groupByWarranty: PropTypes.bool,
    groupByColumn: PropTypes.string,
    groupByColumnOrder: PropTypes.arrayOf(PropTypes.string),
};